import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Brain,
  Heart,
  Droplets,
  TrendingUp,
  Activity,
  Clock,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { AdminDashboardStats, ActivityItem } from '../../types';

export const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashboardRes, activityRes] = await Promise.all([
        adminAPI.getDashboard(),
        adminAPI.getActivity(10),
      ]);

      if (dashboardRes.success) {
        setStats(dashboardRes.data.statistics);
      }
      if (activityRes.success) {
        setActivity(activityRes.data.activity);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `hace ${diffMins}m`;
    if (diffHours < 24) return `hace ${diffHours}h`;
    if (diffDays < 7) return `hace ${diffDays}d`;
    return date.toLocaleDateString('es-ES');
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'headache': return <Brain size={16} className="text-orange-500" />;
      case 'tension': return <Heart size={16} className="text-red-500" />;
      case 'lloro': return <Droplets size={16} className="text-blue-500" />;
      case 'user_register': return <Users size={16} className="text-green-500" />;
      default: return <Activity size={16} />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Dashboard</h1>
        <p className="text-[var(--color-text-muted)]">Vista general del sistema</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Users */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Users size={24} className="text-blue-500" />
            </div>
            <span className="text-xs text-green-500 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-full">
              +{stats?.users.recent || 0} este mes
            </span>
          </div>
          <p className="text-2xl font-bold text-[var(--color-text)]">{stats?.users.total || 0}</p>
          <p className="text-sm text-[var(--color-text-muted)]">Usuarios totales</p>
        </motion.div>

        {/* Headaches */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <Brain size={24} className="text-orange-500" />
            </div>
            <span className="text-xs text-orange-500 bg-orange-100 dark:bg-orange-900/30 px-2 py-1 rounded-full">
              +{stats?.headaches.recent || 0} este mes
            </span>
          </div>
          <p className="text-2xl font-bold text-[var(--color-text)]">{stats?.headaches.total || 0}</p>
          <p className="text-sm text-[var(--color-text-muted)]">Dolores de cabeza</p>
        </motion.div>

        {/* Tensions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <Heart size={24} className="text-red-500" />
            </div>
            <span className="text-xs text-red-500 bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded-full">
              +{stats?.tensions.recent || 0} este mes
            </span>
          </div>
          <p className="text-2xl font-bold text-[var(--color-text)]">{stats?.tensions.total || 0}</p>
          <p className="text-sm text-[var(--color-text-muted)]">Tensiones</p>
          {stats?.tensions.averages && (
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              Media: {stats.tensions.averages.avg_systolic}/{stats.tensions.averages.avg_diastolic} mmHg
            </p>
          )}
        </motion.div>

        {/* Lloros */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Droplets size={24} className="text-blue-500" />
            </div>
            <span className="text-xs text-blue-500 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-full">
              +{stats?.lloros.recent || 0} este mes
            </span>
          </div>
          <p className="text-2xl font-bold text-[var(--color-text)]">{stats?.lloros.total || 0}</p>
          <p className="text-sm text-[var(--color-text-muted)]">Lloros</p>
        </motion.div>
      </div>

      {/* Plan Distribution */}
      {stats?.planDistribution && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="card p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <span style={{ fontSize: '20px' }}>&#x1F451;</span>
            <h2 className="font-semibold text-[var(--color-text)]">Distribucion de Planes</h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 rounded-xl bg-gray-100 dark:bg-gray-800/50">
              <p className="text-2xl font-bold text-gray-500">{stats.planDistribution.free}</p>
              <p className="text-xs text-[var(--color-text-muted)]">Free</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-purple-100 dark:bg-purple-800/30">
              <p className="text-2xl font-bold text-purple-500">{stats.planDistribution.trial}</p>
              <p className="text-xs text-[var(--color-text-muted)]">Trial</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-yellow-100 dark:bg-yellow-800/30">
              <p className="text-2xl font-bold text-yellow-500">{stats.planDistribution.premium}</p>
              <p className="text-xs text-[var(--color-text-muted)]">Premium</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Intensity Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={20} className="text-[var(--color-accent)]" />
            <h2 className="font-semibold text-[var(--color-text)]">Distribución de Intensidad</h2>
          </div>
          <div className="space-y-3">
            {stats?.intensityDistribution?.map((item) => (
              <div key={item.intensity}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[var(--color-text)] capitalize">{item.intensity}</span>
                  <span className="text-[var(--color-text-muted)]">{item.count} ({item.percentage}%)</span>
                </div>
                <div className="h-2 bg-[var(--color-bg)] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${item.percentage}%` }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    className={`h-full rounded-full ${
                      item.intensity === 'leve' ? 'bg-green-500' :
                      item.intensity === 'moderada' ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                  />
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <Clock size={20} className="text-[var(--color-accent)]" />
            <h2 className="font-semibold text-[var(--color-text)]">Actividad Reciente</h2>
          </div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {activity.map((item, index) => (
              <div
                key={`${item.type}-${item.id}-${index}`}
                className="flex items-start gap-3 p-3 rounded-lg bg-[var(--color-bg)]"
              >
                <div className="w-8 h-8 rounded-full bg-[var(--color-surface)] flex items-center justify-center flex-shrink-0">
                  {getActivityIcon(item.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--color-text)] truncate">
                    <span className="font-medium">{item.user_name}</span>
                  </p>
                  <p className="text-xs text-[var(--color-text-muted)]">{item.description}</p>
                </div>
                <span className="text-xs text-[var(--color-text-muted)] flex-shrink-0">
                  {formatDate(item.created_at)}
                </span>
              </div>
            ))}
            {activity.length === 0 && (
              <p className="text-center text-[var(--color-text-muted)] py-8">
                No hay actividad reciente
              </p>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
